import { type Static, Type } from "@sinclair/typebox";
import { Value } from "@sinclair/typebox/value";
import { canonicalCoachOnboardingReasonIsSafe } from "./canonical-coach-onboarding.js";
import { CoachProfileSchema } from "./coach.js";

const CanonicalCoachProfileMaintenanceIdempotencyKeySchema = Type.String({
  minLength: 8,
  maxLength: 96,
  pattern: "^[A-Za-z0-9][A-Za-z0-9._:-]*$",
});

export const CANONICAL_COACH_PROFILE_MAINTENANCE_IDEMPOTENCY_PREFIX = "coach-profile-maintenance:";

export function canonicalCoachProfileMaintenanceIdempotencyKey(rootKey: string): string {
  return `${CANONICAL_COACH_PROFILE_MAINTENANCE_IDEMPOTENCY_PREFIX}${rootKey}`;
}

/**
 * Changes only coach-specific presentation and level assignment. Natural-person
 * name/phone, employment, role, profile status and account state are deliberately
 * absent so this command cannot become a second personnel lifecycle path.
 */
export const UpdateCanonicalCoachProfileCommandSchema = Type.Object(
  {
    profileId: Type.String({ minLength: 1, maxLength: 64 }),
    expectedProfileVersion: Type.Integer({ minimum: 1, maximum: Number.MAX_SAFE_INTEGER - 1 }),
    expectedCoachConfigurationVersion: Type.Integer({
      minimum: 1,
      maximum: Number.MAX_SAFE_INTEGER - 1,
    }),
    displayName: Type.String({ minLength: 1, maxLength: 80 }),
    avatarUrl: Type.Union([Type.Null(), Type.String({ minLength: 1, maxLength: 500 })]),
    levelId: Type.String({ minLength: 1, maxLength: 64 }),
    reason: Type.String({ minLength: 2, maxLength: 500 }),
    idempotencyKey: CanonicalCoachProfileMaintenanceIdempotencyKeySchema,
  },
  { additionalProperties: false },
);
export type UpdateCanonicalCoachProfileCommand = Static<
  typeof UpdateCanonicalCoachProfileCommandSchema
>;

export const CanonicalCoachProfileMaintenanceResultSchema = Type.Object(
  {
    idempotencyKey: CanonicalCoachProfileMaintenanceIdempotencyKeySchema,
    profile: CoachProfileSchema,
    coachConfigurationVersion: Type.Integer({ minimum: 1 }),
    completedAt: Type.String({ format: "date-time" }),
  },
  { additionalProperties: false },
);
export type CanonicalCoachProfileMaintenanceResult = Static<
  typeof CanonicalCoachProfileMaintenanceResultSchema
>;

export function isUpdateCanonicalCoachProfileCommand(
  value: unknown,
): value is UpdateCanonicalCoachProfileCommand {
  return (
    Value.Check(UpdateCanonicalCoachProfileCommandSchema, value) &&
    value.profileId === value.profileId.trim() &&
    value.displayName === value.displayName.trim() &&
    value.levelId === value.levelId.trim() &&
    value.reason === value.reason.trim() &&
    (value.avatarUrl === null || value.avatarUrl === value.avatarUrl.trim()) &&
    canonicalCoachOnboardingReasonIsSafe(value.reason)
  );
}

export function isCanonicalCoachProfileMaintenanceResult(
  value: unknown,
): value is CanonicalCoachProfileMaintenanceResult {
  return (
    Value.Check(CanonicalCoachProfileMaintenanceResultSchema, value) &&
    Number.isSafeInteger(value.profile.version) &&
    Number.isSafeInteger(value.coachConfigurationVersion)
  );
}

export function canonicalCoachProfileMaintenanceResultMatchesCommand(
  result: CanonicalCoachProfileMaintenanceResult,
  command: UpdateCanonicalCoachProfileCommand,
): boolean {
  return (
    result.idempotencyKey === command.idempotencyKey &&
    result.profile.id === command.profileId &&
    result.profile.version === command.expectedProfileVersion + 1 &&
    result.profile.displayName === command.displayName &&
    result.profile.avatarUrl === command.avatarUrl &&
    result.profile.levelId === command.levelId &&
    result.coachConfigurationVersion === command.expectedCoachConfigurationVersion + 1
  );
}
